import React, { useEffect, useRef, useState } from 'react';
import './Rueda.css';
import { Icono } from './iconos';

/**
 * Ruleta de verdad: una rueda en SVG que gira y frena sobre el gajo premiado.
 *
 * El resultado se decide ANTES de girar y la rueda se lleva hasta ahí. Es al revés de lo que
 * parece —no es que gira y "cae" en algo— porque el efecto tiene que aterrizar exactamente en el
 * gajo que corresponde, sin depender de dónde termine una animación.
 */

const RADIO = 150;
const CENTRO = 160;

/** Coordenada del borde de la rueda para un ángulo dado. */
function punto(grados, radio = RADIO) {
    const rad = ((grados - 90) * Math.PI) / 180;
    return [CENTRO + radio * Math.cos(rad), CENTRO + radio * Math.sin(rad)];
}

/** Path de un gajo: un triángulo curvo desde el centro hasta el borde. */
function gajoPath(desde, hasta) {
    const [x1, y1] = punto(desde);
    const [x2, y2] = punto(hasta);
    const largo = hasta - desde > 180 ? 1 : 0;
    return `M ${CENTRO} ${CENTRO} L ${x1} ${y1} A ${RADIO} ${RADIO} 0 ${largo} 1 ${x2} ${y2} Z`;
}

export default function Rueda({ items, ganadorIdx, girando, onFin, colorDe }) {
    const [angulo, setAngulo] = useState(0);
    const [aterrizo, setAterrizo] = useState(false);
    const yaGiro = useRef(false);
    const seg = 360 / items.length;

    useEffect(() => {
        if (!girando || ganadorIdx == null || yaGiro.current) return;
        yaGiro.current = true;
        setAterrizo(false);

        // Vueltas completas + lo justo para dejar el gajo ganador arriba, bajo la aguja.
        const vueltas = 5 + Math.floor(Math.random() * 3);
        const destino = 360 * vueltas - (ganadorIdx * seg + seg / 2);
        // Un pelín de desvío dentro del gajo para que no frene siempre clavado en el centro.
        const desvio = (Math.random() - 0.5) * seg * 0.5;

        // El navegador necesita un frame con el valor viejo para animar la transición.
        requestAnimationFrame(() => setAngulo(destino + desvio));

        const t = setTimeout(() => { setAterrizo(true); onFin && onFin(); }, 4200);
        return () => clearTimeout(t);
    }, [girando, ganadorIdx, seg, onFin]);

    return (
        <div className={`rueda-wrap${girando ? ' girando' : ''}${aterrizo ? ' aterrizo' : ''}`}>
            <div className="rueda-halo" />
            <div className="rueda-aguja" aria-hidden="true" />

            <svg viewBox="0 0 320 320" className="rueda-svg"
                style={{ transform: `rotate(${angulo}deg)` }}>
                <defs>
                    {/* Un degrade por gajo: el color plano se veia chato al lado del aro dorado */}
                    {items.map((item, i) => {
                        const c = colorDe(item, i);
                        return (
                            <linearGradient key={`g${i}`} id={`gajo-${i}`} x1="0" y1="0" x2="0" y2="1">
                                <stop offset="0%" stopColor={c.fondo} stopOpacity="1" />
                                <stop offset="100%" stopColor="#0a0f1a" stopOpacity="0.75" />
                            </linearGradient>
                        );
                    })}
                    <linearGradient id="rueda-aro" x1="0" y1="0" x2="1" y2="1">
                        <stop offset="0%" stopColor="#fff3c4" />
                        <stop offset="45%" stopColor="#D4A845" />
                        <stop offset="55%" stopColor="#8a6a1f" />
                        <stop offset="100%" stopColor="#ffd166" />
                    </linearGradient>
                    <radialGradient id="rueda-centro">
                        <stop offset="0%" stopColor="#ffd166" />
                        <stop offset="70%" stopColor="#D4A845" />
                        <stop offset="100%" stopColor="#8a6a1f" />
                    </radialGradient>
                    <filter id="rueda-sombra">
                        <feDropShadow dx="0" dy="2" stdDeviation="3" floodOpacity="0.55" />
                    </filter>
                </defs>

                {items.map((item, i) => {
                    const desde = i * seg;
                    const hasta = (i + 1) * seg;
                    const medio = desde + seg / 2;
                    const [ix, iy] = punto(medio, RADIO * 0.66);
                    return (
                        <g key={item.id}>
                            <path d={gajoPath(desde, hasta)} fill={`url(#gajo-${i})`}
                                stroke="#0a0f1a" strokeWidth="2" />
                            <Icono nombre={item.icono} cx={ix} cy={iy} size={40} rotacion={medio} />
                        </g>
                    );
                })}

                {/* Tachas del borde, como las de una ruleta real */}
                {items.map((_, i) => {
                    const [tx, ty] = punto(i * seg, RADIO - 4);
                    return <circle key={`t${i}`} cx={tx} cy={ty} r="3.5" fill="#ffd166" opacity="0.9" />;
                })}

                <circle cx={CENTRO} cy={CENTRO} r={RADIO + 6} fill="none" stroke="url(#rueda-aro)" strokeWidth="9" />
                <circle cx={CENTRO} cy={CENTRO} r={RADIO + 11} fill="none" stroke="#0a0f1a" strokeWidth="3" opacity="0.8" />
                <circle cx={CENTRO} cy={CENTRO} r="30" fill="url(#rueda-centro)" filter="url(#rueda-sombra)" />
                <circle cx={CENTRO} cy={CENTRO} r="12" fill="#0a0f1a" />
            </svg>

            {aterrizo && <div className="rueda-destello" aria-hidden="true" />}
        </div>
    );
}
